// Shared across aggregate bundle copies: one pending scan per key; actions take priority.
type Pending = { run: () => Promise<unknown>; timer?: ReturnType<typeof setTimeout>; running: boolean;
    again: boolean; promise: Promise<unknown>; resolve: (value: unknown) => void; reject: (error: unknown) => void };
interface BackgroundState { actions: number; released: Set<() => void>; scans: Map<string, Pending>; yieldedAt?: number }
const host = globalThis as typeof globalThis & { __ftJanusBackground?: BackgroundState };
const state: BackgroundState = host.__ftJanusBackground ??= { actions: 0, released: new Set(), scans: new Map() };

/** Superseded background work is retired without a player refusal or retry. */
export class BackgroundSuperseded extends Error {}

/** All ability dispatches and delivery attempts use the same nesting-safe signal. */
export function beginAbilityAction(): () => void {
    state.actions++;
    let held = true;
    return () => {
        if (!held) return;
        held = false;
        if (--state.actions === 0) { const waiting = [...state.released]; state.released.clear(); for (const resume of waiting) resume(); }
    };
}

/** A task boundary, not a microtask: fetch bodies, input and timers run before the next chunk. */
export async function backgroundCheckpoint(): Promise<void> {
    while (state.actions) await new Promise<void>(resolve => state.released.add(resolve));
    // Small reads share an 8 ms chunk; a timer per ability would pay the browser's timer clamp thousands of times.
    if (performance.now() - (state.yieldedAt ?? 0) >= 8) {
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        state.yieldedAt = performance.now();
        while (state.actions) await new Promise<void>(resolve => state.released.add(resolve));
    }
}

/** Notifications replace pending work; a running scan gets only one latest follow-up. */
export function scheduleBackgroundScan<T>(key: string, run: () => Promise<T>, delayMs = 0): Promise<T> {
    const existing = state.scans.get(key);
    if (existing) {
        existing.run = run;
        if (existing.running) existing.again = true;
        else { clearTimeout(existing.timer); existing.timer = setTimeout(() => { void drain(existing); }, delayMs); }
        return existing.promise as Promise<T>;
    }
    let resolve!: (value: unknown) => void, reject!: (error: unknown) => void;
    const promise = new Promise<unknown>((yes, no) => { resolve = yes; reject = no; });
    const pending: Pending = { run, running: false, again: false, promise, resolve, reject };
    state.scans.set(key, pending);
    pending.timer = setTimeout(() => { void drain(pending); }, delayMs);
    return promise as Promise<T>;

    async function drain(job: Pending): Promise<void> {
        job.running = true;
        try {
            let result: unknown;
            do {
                await backgroundCheckpoint(); job.again = false;
                try { result = await job.run(); }
                catch (error) { if (!job.again) throw error; }
            } while (job.again);
            job.resolve(result);
        } catch (error) { job.reject(error); }
        finally { state.scans.delete(key); }
    }
}
